import React, { useLayoutEffect, useRef, useState } from 'react'
import StoreBox from './StoreBox'
import LoadingSpinner from './LoadingSpinner'
import { SearchPlaceResponse } from '../api/map'
import { ImportedPlaceSummary } from '../api/bookmark'
import { kakaoApi } from '../api/kakao'
import { usePlaceSuggest } from '../hooks/usePlaceSuggest'
import { useKeyboardInset } from '../hooks/useKeyboardInset'

interface ImportPlaceSearchResultsProps {
  /** 입력칸 글자. 링크가 아닐 때만 넘긴다 */
  keyword: string
  /** 고른 가게. 미등록(카카오) 결과는 등록을 마친 뒤 우리 place id 로 넘어온다 */
  onPick: (place: ImportedPlaceSummary) => void
}

/**
 * 빈 결과 문구. 검색 화면(Search.tsx)과 같은 모양 — 16px, 결과 영역 한가운데.
 * 부르는 쪽이 입력칸 아래 남은 자리를 relative 로 채워 줘야 한다.
 *
 * 키보드에 가린 부분은 빼고 가운데를 잡는다. 앱은 키보드가 떠도 웹뷰를
 * 줄이지 않으므로(acquireNoKeyboardResize) 영역이 키보드 밑까지 이어진다.
 * 그대로 가운데 두면 실기기에서 아래로 처져 보였다 — 시뮬레이터는 맥
 * 키보드라 화면 키보드가 안 떠서 멀쩡해 보인다.
 */
const EmptyResult: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const keyboard = useKeyboardInset()
  /** 이 영역 아래쪽 중 키보드에 가린 높이 */
  const [hidden, setHidden] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const visibleBottom = window.innerHeight - keyboard.padding
    const rect = el.getBoundingClientRect()
    setHidden(Math.max(0, Math.round(rect.bottom - visibleBottom)))
  }, [keyboard.padding])

  return (
    <div
      ref={ref}
      className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center"
      style={{
        paddingBottom: hidden,
        transition: `padding-bottom ${keyboard.duration}ms ease-out`,
      }}
    >
      <p className="font-pretendard font-regular text-16px leading-180% text-[#6B5E46] text-center">
        검색 결과가 없어요
      </p>
    </div>
  )
}

/**
 * '장소 가져오기' 입력칸에 링크 대신 가게 이름을 넣었을 때의 검색 결과.
 *
 * 온보딩(OnboardingImportRank)과 플레이트리스트(ImportListContent)가 같이 쓴다.
 * 행 모양은 검색 화면과 같은 StoreBox 를 쓰되, 여기서는 고르기만 하므로
 * +·북마크 버튼은 감춘다 (누르면 리뷰 화면으로 가 버린다).
 */
const ImportPlaceSearchResults: React.FC<ImportPlaceSearchResultsProps> = ({
  keyword,
  onPick,
}) => {
  const { results, isLoading } = usePlaceSuggest(keyword)
  /** 등록 중인 행 — 두 번 눌러 두 번 등록되지 않게 */
  const [pickingKey, setPickingKey] = useState<string | null>(null)

  const handlePick = async (place: SearchPlaceResponse, key: string) => {
    if (pickingKey) return

    if (place.id) {
      onPick({ id: Number(place.id), name: place.name, address: place.address })
      return
    }
    if (!place.kakao) return

    setPickingKey(key)
    try {
      /*
       * 미등록(카카오) 결과는 먼저 우리 DB 에 등록한다.
       * 실패하면 null — 음식점·카페·술집이 아니면 서버가 422 와 함께
       * "음식점·카페·술집만 등록할 수 있습니다" 를 주고, apiClient 인터셉터가
       * 그 말을 토스트로 띄운다. 여기서 또 띄우면 겹친다.
       */
      const reg = await kakaoApi.registerKakaoPlace(place.kakao)
      if (reg?.place_id) {
        onPick({ id: reg.place_id, name: place.name, address: place.address })
      }
    } finally {
      setPickingKey(null)
    }
  }

  if (results.length === 0) {
    return isLoading ? (
      <div className="w-full flex items-center justify-center py-40px">
        <LoadingSpinner size={28} borderWidth={4} />
      </div>
    ) : (
      <EmptyResult />
    )
  }

  return (
    <div className="flex flex-col gap-24px pb-16px">
      {results.map((place, idx) => {
        const key = place.id ?? `kakao-${place.kakao?.id ?? idx}`
        return (
          <div
            key={key}
            className={pickingKey === key ? 'opacity-40' : undefined}
          >
            <StoreBox
              id={place.id}
              name={place.name}
              address={place.address}
              hideActionButtons
              onCardClick={() => handlePick(place, key)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default ImportPlaceSearchResults
